import React, { useMemo } from 'react';
import { Box, Divider, Grid, Typography } from 'housecall-ui';

import { useOrgData } from '../hooks/useOrgData';
import { ContactDetailsValues, State } from '../state/types';
import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';
import { getQuantityFromItem, hasPaymentStep, isPricingFormItem, isServiceCartItem } from '../state/utils';
import { getDepositAmount, getDepositLabel } from '../Steps/PaymentStep/utils';
import { calculateSubTotalPrice, calculateTotalPrice } from '../utils/calculatePrice';
import { generateConfirmationArrivalSubtitle, generateConfirmationContactSubtitle } from '../utils/generateSubtitle';
import { roundNumber } from '../utils/roundPrice';

import { CartItemDisplay } from './Cart/CartItemDisplay';
import { calculatePricingFormTotalPrice, generateDescription } from './form/utils';
import ConfirmationElement from './ConfirmationElement';

const prepareServices = ({ cart }: State) => {
  return cart.filter(isServiceCartItem).map((cartItem) => ({
    title: cartItem.service.name,
    quantity: getQuantityFromItem(cartItem),
    price: cartItem.service.price,
    boldPrice: true,
  }));
};

const preparePricingForms = ({ cart }: State) => {
  return cart.filter(isPricingFormItem).map((cartItem) => ({
    title: cartItem.pricingForm.name,
    quantity: 1,
    price: calculatePricingFormTotalPrice(cartItem.pricingForm, cartItem.values),
    boldPrice: true,
    description: generateDescription(cartItem.pricingForm, cartItem.values),
  }));
};

const ContactDetailsContent = ({ contactDetailsValues }: { contactDetailsValues: ContactDetailsValues }) => {
  const { firstName, lastName, phone, email } = contactDetailsValues;
  return (
    <ConfirmationElement
      title="Your contact details"
      subtitle={generateConfirmationContactSubtitle({ firstName, lastName, email, phone })}
    />
  );
};

const ConfirmationSummary = () => {
  const { state } = useOnlineBookingReducer();
  const { token, cart, arrivalWindow, contactDetails, tripCharge } = state;
  const { data: orgData, isUnscheduled } = useOrgData({ token });
  const { addressLine1, addressLine2, city, state: usState, zipCode } = contactDetails.values;

  const isExactTime = orgData?.defaultArrivalWindow === 0;

  const servicesCards = prepareServices(state);
  const pricingForms = preparePricingForms(state);

  const orgTax = orgData && 'taxInfo' in orgData ? orgData?.taxInfo : null;

  const subTotal = calculateSubTotalPrice(cart) + (tripCharge?.tripChargeFee || 0);
  const total = calculateTotalPrice(cart, orgTax?.rate || null) + (tripCharge?.tripChargeFee || 0);

  const taxName = `Tax, ${orgTax?.name || 'N/A'} (${roundNumber((orgTax?.rate || 0) * 100)}%):`;

  const arrivalSubtitle = useMemo(
    () =>
      generateConfirmationArrivalSubtitle({ arrivalWindow, isExactTime, timeZone: orgData?.timeZone, isUnscheduled }),
    [arrivalWindow, isExactTime, orgData?.timeZone, isUnscheduled]
  );
  const requiresPayment = Boolean(orgData && hasPaymentStep(cart, orgData, state.tripCharge));
  const showDeposit = orgData?.paymentSettings?.config === 'deposit' && requiresPayment;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box display="flex" flexDirection="column">
          <Typography variant="subtitle1" gutterBottom>
            Selected services
          </Typography>
          {pricingForms.map((item) => (
            <CartItemDisplay key={`${item.title}-${item.price}`} {...item} />
          ))}
          {servicesCards.map((item) => (
            <CartItemDisplay key={`${item.title}-${item.price}`} {...item} />
          ))}
          {tripCharge && tripCharge.tripChargeFee > 0 && (
            <CartItemDisplay
              testId="trip-charge-fee"
              boldPrice
              title={tripCharge.tripChargeFeeName}
              price={tripCharge.tripChargeFee}
            />
          )}
          {subTotal > 0 ? (
            <>
              <CartItemDisplay title="Subtotal:" boldPrice price={subTotal} />
              <CartItemDisplay title={taxName} boldPrice price={roundNumber(total - subTotal)} />
              <CartItemDisplay bold title="Total:" price={total} />
              {showDeposit && (
                <CartItemDisplay
                  bold
                  testId="deposit-amount"
                  title={getDepositLabel(orgData.paymentSettings)}
                  price={getDepositAmount(orgData.paymentSettings, total)}
                />
              )}
            </>
          ) : null}
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {isUnscheduled ? (
        <ContactDetailsContent contactDetailsValues={contactDetails.values} />
      ) : (
        <ConfirmationElement title="Arrival time" subtitle={arrivalSubtitle} isWide={false} />
      )}
      <ConfirmationElement
        title="Location"
        subtitle={`${addressLine2} ${addressLine1}, ${city}, ${usState} ${zipCode}`}
      />
      <Grid item xs={12}>
        <Divider />
      </Grid>
      {isUnscheduled ? (
        <ConfirmationElement title="Arrival time" subtitle={arrivalSubtitle} isWide />
      ) : (
        <ContactDetailsContent contactDetailsValues={contactDetails.values} />
      )}
    </Grid>
  );
};

export default ConfirmationSummary;
